import { SettingsService } from '@starsoft/common/services';
import { Image } from '../Image';
import { LottieOrImageProps } from './props';
import styles from './styles.module.scss';

/**
 * A component that renders either a Lottie animation or an image.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.image] - URL of the image to display
 * @param {RefObject<HTMLDivElement>} [props.lottie] - Ref object for Lottie animation container
 * @param {Object} [props.classList] - CSS class names for styling
 * @param {string} [props.classList.container] - Class name for the container div
 * @param {string} [props.classList.image] - Class name for the image element
 * @param {string} [props.classList.lottie] - Class name for the Lottie container
 *
 * @example
 * ```jsx
 * // With image
 * <LottieOrImage
 *   image="/path/to/image.jpg"
 *   classList={{
 *     container: "custom-container",
 *     image: "custom-image"
 *   }}
 * />
 *
 * // With Lottie animation
 * const lottieRef = useRef(null);
 * <LottieOrImage
 *   lottie={lottieRef}
 *   classList={{
 *     container: "custom-container",
 *     lottie: "custom-lottie"
 *   }}
 * />
 * ```
 */
export function LottieOrImage({
  image,
  lottie,
  icon,
  classList,
}: LottieOrImageProps) {
  const settings = SettingsService.env.S3_BUCKET_BASE_URL;

  const getPreferStaticImages = () => {
    return !settings.includes('ice-game.s3');
  };

  const preferStaticImages: boolean = getPreferStaticImages();

  const { lottieAnimationRef } = lottie;

  if (preferStaticImages) {
    return (
      <div
        className={` ${styles.container} ${
          icon ? styles['container--icon'] : ''
        } ${classList?.container ? classList?.container : ''}`}
      >
        {image && (
          <Image
            src={image}
            preventContext
            className={`${classList?.image ? classList?.image : ''}`}
            alt={image}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={` ${styles.container} ${
        icon ? styles['container--icon'] : ''
      } ${classList?.container ? classList?.container : ''}`}
    >
      <div
        className={`${styles.container} ${classList?.lottie ? classList?.lottie : ''}`}
        ref={lottieAnimationRef}
      />
    </div>
  );
}
